// Google Consent Mode v2 — single source of truth for the consent decision.
//
// The decision is persisted under one key and applied in TWO places, in
// this order:
//
//   1. The inline snippet in public/index.html, which runs synchronously
//      in <head> BEFORE the GTM loader. A returning visitor's saved
//      decision has to be on the dataLayer before gtm.js starts firing
//      tags, otherwise the tags fire in the default (denied) state and
//      the visit is recorded cookieless no matter what the user chose
//      last time. `wait_for_update: 500` does NOT save us here — React
//      needs far longer than 500ms to mount on a mid-range phone.
//
//   2. CookieBanner, for the first-visit decision (there is nothing
//      stored yet, so only a click can resolve it).
//
// Keep STORAGE_KEY and the payload shape in sync with the inline snippet
// in public/index.html. They are duplicated on purpose: the snippet
// cannot import from the bundle, because the whole point is that it runs
// before the bundle exists.
export const STORAGE_KEY = "spotie-cookie-consent";

export const GRANTED = "granted";
export const DENIED = "denied";

// The banner is all-or-nothing ("Accept all" / "Decline"), so every
// category moves together. If the banner ever grows per-category
// toggles, this is the one place that has to learn about them — do not
// grant a category the user did not choose.
export const consentPayload = (decision) => {
  const value = decision === GRANTED ? GRANTED : DENIED;
  return {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  };
};

export const readStoredConsent = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === GRANTED || stored === DENIED ? stored : null;
  } catch (e) {
    // Private mode / blocked storage: treat as "no decision yet".
    return null;
  }
};

export const writeStoredConsent = (decision) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, decision);
  } catch (e) {
    // Still update consent for this page view even if we can't persist.
  }
};

// Pushes the Consent Mode update. Safe before gtag exists — the inline
// snippet defines gtag in <head>, so by the time any React code runs it
// is always present; the guard is for tests and SSR.
export const applyConsent = (decision) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }
  window.gtag("consent", "update", consentPayload(decision));
  return true;
};

// True when the inline snippet already restored a stored decision on this
// page load, so the banner doesn't push a second, identical update.
export const alreadyRestored = () =>
  typeof window !== "undefined" && window.__spotieConsentRestored === true;
